import { z } from 'zod';

import { AwardType } from '@occc-app/offerHelpers/types';
import {
  Earning_Mechanism_Product_Counting_Attribute,
  Constraint_Purchase_Threshold_Counting_Attribute,
  OfferFragment,
  EarningMechanismVarietyPurchaseFragment,
  EarningMechanismVarietyPurchaseOverTimeFragment,
  ProductGroupFragment,
} from '@occc/graphql';
import {
  LimitTimePeriod,
  getAwardCountInitialValues,
  getProgressPerPeriodLimitInitialValues,
  getProgressPerTransactionLimitInitialValues,
  hasProgressPerPeriodLimit,
  hasProgressPerTransactionLimit,
} from '@occc/feat-limits';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { awardHelper } from '@occc-app/offerHelpers/utils/awardHelper';
import {
  FulfillmentChannel,
  getDefaultTimezone,
  getFulfillmentChannelInitialValues,
  getMinimumPurchaseConstraintInitialValues,
  getPaymentMethodInitialValues,
  getRequiresActivationWithLimitsInitialValues,
  transformAwardRestrictionsToFormValues,
  transformOfferToFulfillmentWindowFormValue,
} from '@occc/feat-constraints';

import { getOfferNameAndDescriptionInitialValues } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionInitialValues';
import { getOfferFulfillmentPriorityInitialValues } from '@occc-app/offerHelpers/utils/getOfferFulfillmentPriorityInitialValues';
import { transformOfferScanDataConfigurationToInitialValues } from '@occc-app/offerHelpers/utils/transformOfferScanDataConfigurationToInitialValues';
import { ChallengeAwardSchema } from '../../../schemas';
import { ChallengeType } from '../../../constants';
import {
  ChallengeConfigureFormInitialValues,
  ChallengeEarningMechanism,
  ChallengeEarningMechanismInitialValues,
  ProductChallengeEarningMechanism,
  ProductChallengeFormValues,
} from '../../../types';
import { transformCustomData } from '../../../../CampaignComponents/components/CustomData/utils';

export const getProductChallengeFormValues = (
  earningMechanism: ChallengeEarningMechanism,
  earningMechanismType: ProductChallengeEarningMechanism,
): ProductChallengeFormValues => {
  const isVarietyPurchaseOverTime =
    earningMechanismHelper.isVarietyPurchaseOverTime(earningMechanism);

  return {
    earningMechanism: earningMechanismType,
    products: earningMechanism.products,
    stayVisibleWhileInProgress: {
      isEnabled: isVarietyPurchaseOverTime && !!earningMechanism.stayVisibleWhileInProgress,
    },
  };
};

const transformChallengeAward = (
  award: Parameters<typeof awardHelper.isPointsAward>[0],
): z.infer<typeof ChallengeAwardSchema> => {
  if (awardHelper.isVouchersAward(award)) {
    return {
      awardType: AwardType.AwardVouchers,
      awardVouchers: award.voucherExpirationDays
        ? {
            voucheredOffer: award.voucheredOffer,
            voucherExpirationDays: award.voucherExpirationDays,
            isEnabled: true,
          }
        : { voucheredOffer: award.voucheredOffer, isEnabled: false },
    };
  }

  return {
    awardType: AwardType.AwardPoints,
    awardPoints: { points: awardHelper.isPointsAward(award) ? award.points : 0 },
  };
};

const transformVarietyPurchase = (earningMechanism: EarningMechanismVarietyPurchaseFragment) => {
  return {
    productChallenge: getProductChallengeFormValues(earningMechanism, 'varietyPurchase'),
    award: transformChallengeAward(earningMechanism.varietyPurchaseAward),
  };
};

const transformVarietyPurchaseOverTime = (
  earningMechanism: EarningMechanismVarietyPurchaseOverTimeFragment,
) => {
  return {
    productChallenge: getProductChallengeFormValues(earningMechanism, 'varietyPurchaseOverTime'),
    award: transformChallengeAward(earningMechanism.varietyPurchaseOverTimeAward),
  };
};

const getConfigureFormInitialValues = (): ChallengeConfigureFormInitialValues => ({
  challengeType: null,
  earningMechanism: {},
  award: {
    awardType: AwardType.AwardPoints,
  },
  productChallenge: {
    products: [{ countProductsBy: Earning_Mechanism_Product_Counting_Attribute.Quantity }],
    earningMechanism: 'varietyPurchase',
  },
  awardCount: {
    isEnabled: false,
    global: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    membership: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    transaction: {
      isEnabled: false,
    },
    transactionFrequency: { isEnabled: false, rows: [{ limit: 1, timePeriod: 'DAY' }] },
  },
  activation: {
    isEnabled: false,
    global: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    membership: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    requiresActivation: {
      isMustFulfillWithinEnabled: false,
      allowMultipleFulfillmentsOnActivation: false,
    },
  },
  fulfillmentWindow: {
    isEnabled: false,
    timezone: getDefaultTimezone(),
  },
  paymentMethodConstraint: {
    isEnabled: false,
  },
  fulfillmentChannelConstraint: {
    isEnabled: false,
    channel: FulfillmentChannel.OrderAheadOnly,
  },
  minimumPurchaseConstraints: {
    isEnabled: false,
    items: [
      {
        countProductsBy: Constraint_Purchase_Threshold_Counting_Attribute.Quantity,
      },
    ],
  },
  awardRestrictions: { isEnabled: false, awards: [] },
  scanDataConfiguration: {
    isEnabled: false,
    fundingSource: '',
    description: '',
    discountAllocation: undefined,
  },
});
const transformChallengeEarningMechanism = (
  earningMechanism: OfferFragment['earningMechanism'],
): ChallengeEarningMechanismInitialValues => {
  if (!earningMechanism) {
    return {};
  }

  if (earningMechanismHelper.isVisits(earningMechanism)) {
    return {
      challengeType: ChallengeType.VisitChallenge,
      visitChallenge: {
        id: earningMechanism.id,
        visitThreshold: earningMechanism.visitThreshold,
      },
      award: transformChallengeAward(earningMechanism.visitsAward),
    };
  }

  if (earningMechanismHelper.isVarietyPurchase(earningMechanism)) {
    return {
      challengeType: ChallengeType.ProductChallenge,
      ...transformVarietyPurchase(earningMechanism),
    };
  }

  if (earningMechanismHelper.isVarietyPurchaseOverTime(earningMechanism)) {
    return {
      challengeType: ChallengeType.ProductChallenge,
      ...transformVarietyPurchaseOverTime(earningMechanism),
    };
  }

  return {};
};

const getActivationSchemaInitialValues = (
  offer: OfferFragment,
  defaultValues: ChallengeConfigureFormInitialValues['activation'],
): NonNullable<ChallengeConfigureFormInitialValues['activation']> =>
  getRequiresActivationWithLimitsInitialValues(offer, defaultValues) as NonNullable<
    ChallengeConfigureFormInitialValues['activation']
  >;

const getAwardCountSchemaInitialValues = (
  offer: OfferFragment,
  defaultValues: ChallengeConfigureFormInitialValues['awardCount'],
): NonNullable<ChallengeConfigureFormInitialValues['awardCount']> => {
  const awardCount = getAwardCountInitialValues(offer, defaultValues, { excludeTotal: true });

  return {
    ...awardCount,
    isEnabled:
      awardCount.isEnabled ||
      hasProgressPerPeriodLimit(offer.limits) ||
      hasProgressPerTransactionLimit(offer.limits),
    progressPerPeriod: getProgressPerPeriodLimitInitialValues(offer),
    progressPerTransaction: getProgressPerTransactionLimitInitialValues(offer),
  } as NonNullable<ChallengeConfigureFormInitialValues['awardCount']>;
};

export const transformOfferToInitialValues = (
  offer?: OfferFragment,
  productGroups?: ProductGroupFragment[],
): ChallengeConfigureFormInitialValues => {
  const values = getConfigureFormInitialValues();

  if (!offer) {
    return values;
  }

  const awardRestrictionsConstraint = offer.constraints.find(
    (constraint) => constraint.__typename === 'ConstraintPurchasableEligibility',
  );

  return {
    ...values,
    ...getOfferNameAndDescriptionInitialValues(offer),
    ...getOfferFulfillmentPriorityInitialValues(offer),
    customData: transformCustomData(offer.customData),
    earningMechanism: {
      id: offer.earningMechanism?.id,
    },
    ...transformChallengeEarningMechanism(offer.earningMechanism),
    activation: getActivationSchemaInitialValues(offer, values.activation),
    awardCount: getAwardCountSchemaInitialValues(offer, values.awardCount),
    ...getMinimumPurchaseConstraintInitialValues(offer, productGroups),
    ...transformOfferToFulfillmentWindowFormValue(offer),
    paymentMethodConstraint: getPaymentMethodInitialValues(offer),
    fulfillmentChannelConstraint: getFulfillmentChannelInitialValues(offer),
    awardRestrictions: awardRestrictionsConstraint
      ? transformAwardRestrictionsToFormValues(awardRestrictionsConstraint)
      : values.awardRestrictions,
    ...transformOfferScanDataConfigurationToInitialValues(offer.scanDataConfiguration),
  };
};
